import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { IonInput } from '@ionic/angular';
import { debounceTime, distinctUntilChanged, Subject } from 'rxjs';
import { IRefNome } from '../../../vistoria/models/tipo-vistoria.interface';

/**
 * Select pesquisável de **seleção múltipla** (PBI 113881 — filtro "Número e
 * nome da linha"). O `app-select-search-input` existente é de seleção única e
 * o `app-status-filter` é múltiplo mas sem campo de busca; este componente
 * cobre a combinação que faltava.
 *
 * Fechado, o campo mostra os itens selecionados; aberto, vira o campo de
 * busca. Com `externalSearch`, o termo digitado é emitido em
 * `externalSearchFunction` (debounce de 300ms) para que a tela refaça a busca
 * no servidor; sem ele, a filtragem é local sobre `allData`.
 */
@Component({
  selector: 'app-select-search-multiple',
  templateUrl: './select-search-multiple.component.html',
  styleUrls: ['./select-search-multiple.component.scss'],
})
export class SelectSearchMultipleComponent
  implements OnInit, OnChanges, OnDestroy
{
  @Input() label: string = '';
  @Input() placeholder: string = 'Digitar ou selecionar';
  @Input() allData: IRefNome[] = [];
  @Input() selected: IRefNome[] = [];
  @Input() disabled: boolean = false;
  @Input() externalSearch: boolean = false;
  @Output() selectedChange = new EventEmitter<IRefNome[]>();
  @Output() externalSearchFunction = new EventEmitter<string>();
  @ViewChild('inputField') inputField?: IonInput;

  filteredData: IRefNome[] = [];
  listOpen: boolean = false;
  searchTerm: string = '';

  private readonly searchSubject = new Subject<string>();

  constructor(private readonly elementRef: ElementRef<HTMLElement>) {}

  get resumoSelecao(): string {
    return this.selected.map((item) => item.nome).join(', ');
  }

  get valorExibido(): string {
    return this.listOpen ? this.searchTerm : this.resumoSelecao;
  }

  ngOnInit(): void {
    this.filteredData = this.allData;

    this.searchSubject
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe((term: string) => {
        if (this.externalSearch) {
          this.externalSearchFunction.emit(term);
          return;
        }
        this.filteredData = this.filtrarLocalmente(term);
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['allData']) {
      this.filteredData = this.externalSearch
        ? this.allData
        : this.filtrarLocalmente(this.searchTerm);
    }

    if (changes['disabled'] && this.disabled) {
      this.closeList();
    }
  }

  // composedPath() é fixado no dispatch: continua valendo mesmo se o clique
  // re-renderizou (e desanexou) a opção clicada.
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    if (!event.composedPath().includes(this.elementRef.nativeElement)) {
      this.closeList();
    }
  }

  @HostListener('focusout', ['$event'])
  onFocusOut(event: FocusEvent): void {
    const destino = event.relatedTarget as Node | null;
    if (destino && !this.elementRef.nativeElement.contains(destino)) {
      this.closeList();
    }
  }

  @HostListener('keydown.escape')
  onEscape(): void {
    this.closeList();
  }

  private filtrarLocalmente(term: string): IRefNome[] {
    const termo = (term || '').toLowerCase();
    if (!termo) return this.allData;
    return this.allData.filter((item) =>
      item.nome.toLowerCase().includes(termo),
    );
  }

  onSearch(valor: string | null | undefined): void {
    this.searchTerm = valor ?? '';
    this.openList();
    this.searchSubject.next(this.searchTerm);
  }

  openList(): void {
    if (this.disabled) return;
    this.listOpen = true;
  }

  closeList(): void {
    if (!this.listOpen) return;
    this.listOpen = false;

    if (this.searchTerm) {
      this.searchTerm = '';
      this.searchSubject.next('');
    }
  }

  // O ion-input envolve o slot `end` num <label>: sem o preventDefault, o
  // clique no chevron é repassado ao input e reabre a lista recém-fechada.
  toggleList(event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    if (this.listOpen) {
      this.closeList();
      return;
    }
    this.openList();
    this.inputField?.setFocus();
  }

  isSelected(option: IRefNome): boolean {
    return this.selected.some((item) => item.id === option.id);
  }

  toggleOption(option: IRefNome): void {
    const proximos = this.isSelected(option)
      ? this.selected.filter((item) => item.id !== option.id)
      : [...this.selected, option];

    this.selected = proximos;
    this.selectedChange.emit(proximos);
  }

  clearAll(event?: Event): void {
    event?.stopPropagation();
    this.selected = [];
    this.selectedChange.emit([]);
  }

  onOptionKeydown(event: KeyboardEvent, option: IRefNome): void {
    if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
      event.preventDefault();
      this.toggleOption(option);
    }
  }

  ngOnDestroy(): void {
    this.searchSubject.complete();
  }
}
